import { ValidatorFn, Validators } from '@angular/forms';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import { Address } from '@principle-theorem/principle-core';
import {
  AccAddressType,
  AddressComponents,
  IAccAddress,
} from '@principle-theorem/principle-core/interfaces';

export class AccAddressFormGroup extends TypedFormGroup<IAccAddress> {
  constructor(type: AccAddressType, initWithValidation = true) {
    const required = initWithValidation ? Validators.required : undefined;

    super({
      type: new TypedFormControl<AccAddressType>(type, required),
      line1: new TypedFormControl<string>('', required),
      line2: new TypedFormControl<string>(''),
      suburb: new TypedFormControl<string>(''),
      city: new TypedFormControl<string>('', required),
      postCode: new TypedFormControl<string>('', required),
      country: new TypedFormControl<string>('', required),
    });
  }

  clearAllValidators(): void {
    this._updateValidators(undefined);
  }

  setAllValidators(): void {
    this._updateValidators({
      type: [Validators.required],
      line1: [Validators.required],
      city: [Validators.required],
      postCode: [Validators.required],
      country: [Validators.required],
    });
  }

  convertAddressComponents(components: AddressComponents): void {
    this.patchValue({
      line1: Address.streetAddressFromComponents(components) ?? '',
      line2: components?.subpremise ?? '',
      suburb: components?.political ?? components?.suburb ?? '',
      city: components?.city ?? components?.state ?? '',
      postCode: components?.postalCode ?? '',
      country: components?.country ?? '',
    });

    this.markAllAsTouched();
    this.updateValueAndValidity();
  }

  get line1RequiredError(): boolean {
    return (
      this.controls.line1.touched && this.controls.line1.hasError('required')
    );
  }

  get cityRequiredError(): boolean {
    return (
      this.controls.city.touched && this.controls.city.hasError('required')
    );
  }

  get postCodeRequiredError(): boolean {
    return (
      this.controls.postCode.touched &&
      this.controls.postCode.hasError('required')
    );
  }

  get countryRequiredError(): boolean {
    return (
      this.controls.country.touched &&
      this.controls.country.hasError('required')
    );
  }

  private _updateValidators(
    validatorsMap: Record<string, ValidatorFn[]> | undefined
  ): void {
    const controlsToUpdate: (keyof IAccAddress)[] = [
      'type',
      'line1',
      'city',
      'postCode',
      'country',
    ];

    controlsToUpdate.forEach((controlName) => {
      const control = this.controls[controlName];

      if (validatorsMap) {
        control.setValidators(validatorsMap[controlName]);
        if (controlName === 'type') {
          control.setValue(AccAddressType.Postal);
        }
      } else {
        control.clearValidators();
        control.setValue(undefined);
      }

      control.updateValueAndValidity();
    });
  }
}
